import { useState } from "react";
import { Link2, Copy, Globe, EyeOff, QrCode, Download, ChevronDown, MessageCircle, MessageSquare } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import api from "../../lib/api";

const TWO_WHEELS_LOGO_URL = "/twowheels-logo.svg";

/**
 * Vermittler-Empfehlungs-Link — einklappbare Karte (analog TransactionCodeCard).
 * Immer sichtbar: Link + „Kopieren" / „Teilen" / WhatsApp / SMS / QR.
 * Beim Aufklappen: Erläuterung, Master-Aktivierung, KPIs & Showcase-Opt-In.
 */
export default function ShareLinkCard({ data, user, onRefresh }) {
    const [copying, setCopying] = useState(false);
    const [optIn, setOptIn] = useState(!!user?.showcase_opt_in);
    const [savingOpt, setSavingOpt] = useState(false);
    const [showQr, setShowQr] = useState(false);
    const [open, setOpen] = useState(false);
    const linkActive = user?.referral_link_enabled !== false; // default ON
    const [savingLinkToggle, setSavingLinkToggle] = useState(false);
    const verified = user?.id_verification_status === "approved";

    if (!data) return null;

    const shareText = `Ich empfehle Dir TwoWheels für Motorräder & Fahrräder:\n${data.share_url}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    const smsUrl = `sms:?&body=${encodeURIComponent(shareText)}`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(data.share_url);
            setCopying(true);
            toast.success("Link in Zwischenablage kopiert");
            setTimeout(() => setCopying(false), 1500);
        } catch {
            toast.error("Bitte Link manuell markieren");
        }
    };

    const share = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: "TwoWheels — Mein Vermittler-Link",
                    text: "Ich empfehle Dir TwoWheels für Motorräder & Fahrräder:",
                    url: data.share_url,
                });
            } catch { /* user cancelled */ }
        } else {
            copy();
        }
    };

    const toggleOptIn = async (next) => {
        if (next && !verified) {
            toast.error("Bitte zuerst die Identitätsprüfung abschließen");
            return;
        }
        setSavingOpt(true);
        try {
            await api.patch("/vermittler/showcase-opt-in", { enabled: next });
            setOptIn(next);
            toast.success(next
                ? "Sie sind nun im öffentlichen Vermittler-Showcase auf /team gelistet"
                : "Aus dem öffentlichen Showcase entfernt");
            onRefresh?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSavingOpt(false);
        }
    };

    const downloadQr = () => {
        const svg = document.querySelector('[data-testid="vermittler-qr-svg"]');
        if (!svg) { toast.error("QR-Code nicht gefunden"); return; }
        const blob = new Blob([new XMLSerializer().serializeToString(svg)], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `twowheels-empfehlung-${data.short_code}.svg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("QR-Code heruntergeladen");
    };

    const toggleLinkActive = async (next) => {
        setSavingLinkToggle(true);
        try {
            await api.patch("/vermittler/referral-link-enabled", { enabled: next });
            toast.success(next ? "Empfehlungs-Link aktiviert" : "Empfehlungs-Link deaktiviert — Besucher landen auf /");
            onRefresh?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSavingLinkToggle(false);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="vermittler-share-link">
            {/* Header — einklappbar */}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-2 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                data-testid="vermittler-share-link-toggle"
            >
                <Link2 className="w-4 h-4 text-[#FFCC00] shrink-0" />
                <span className="font-display font-bold text-sm">Ihr persönlicher Empfehlungs-Link</span>
                {linkActive ? (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-green-100 text-green-800 rounded-sm" data-testid="link-active-badge">Aktiv</span>
                ) : (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-gray-200 text-gray-700 rounded-sm" data-testid="link-inactive-badge">Deaktiviert</span>
                )}
                <ChevronDown className={`w-4 h-4 ml-auto text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {/* Immer sichtbar: Link + Aktionen */}
            <div className="px-4 pb-4">
                <div className="flex items-stretch gap-2 mb-2">
                    <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-sm font-mono-num text-xs sm:text-sm truncate" data-testid="share-link-url">
                        {data.share_url}
                    </div>
                    <Button onClick={copy} variant="outline" size="sm" className="rounded-sm font-bold uppercase tracking-wider shrink-0" data-testid="share-link-copy">
                        <Copy className="w-3.5 h-3.5 mr-1" /> {copying ? "✓" : "Kopieren"}
                    </Button>
                    <Button onClick={share} size="sm" className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold uppercase tracking-wider rounded-sm shrink-0" data-testid="share-link-share">
                        Teilen
                    </Button>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-sm bg-[#25D366] text-white px-3 py-1.5 text-xs font-bold hover:bg-[#1EBE5A]"
                        data-testid="share-link-whatsapp"
                    >
                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                    </a>
                    <a
                        href={smsUrl}
                        className="inline-flex items-center gap-1.5 rounded-sm bg-[#111827] text-white px-3 py-1.5 text-xs font-bold hover:bg-black"
                        data-testid="share-link-sms"
                    >
                        <MessageSquare className="w-3.5 h-3.5" /> SMS
                    </a>
                    <button
                        onClick={() => setShowQr((v) => !v)}
                        className="inline-flex items-center gap-1.5 rounded-sm border border-gray-300 px-3 py-1.5 text-xs font-bold hover:border-[#111827]"
                        data-testid="share-link-qr-toggle"
                    >
                        <QrCode className="w-3.5 h-3.5" /> QR
                    </button>
                </div>

                {/* QR-Code panel — collapsible */}
                {showQr && (
                    <div className="mt-3 p-4 border border-gray-200 rounded-sm bg-gray-50 flex flex-col sm:flex-row items-center gap-4" data-testid="vermittler-qr-panel">
                        <div className="bg-white p-3 rounded-sm border border-gray-200">
                            <QRCodeSVG
                                value={data.share_url}
                                size={160}
                                level="H"
                                includeMargin={false}
                                bgColor="#FFFFFF"
                                fgColor="#111827"
                                imageSettings={{
                                    src: TWO_WHEELS_LOGO_URL,
                                    height: 36,
                                    width: 36,
                                    excavate: true,
                                }}
                                data-testid="vermittler-qr-svg"
                            />
                        </div>
                        <div className="flex-1 text-center sm:text-left">
                            <div className="font-bold text-sm mb-1">QR-Code Ihres Empfehlungs-Links</div>
                            <p className="text-xs text-gray-600 mb-3">
                                Branded mit TwoWheels-Logo im Zentrum. Drucken Sie diesen Code auf Visitenkarten, Flyer oder Werkstatt-Aufsteller.
                                Käufer scannen ihn → <span className="font-mono-num">{data.short_code}</span> wird
                                ihrem Cookie zugeordnet → Sie bekommen automatisch <strong>50 Punkte</strong> bei jedem Kauf.
                            </p>
                            <Button onClick={downloadQr} size="sm" className="bg-[#111827] hover:bg-black text-white rounded-sm font-bold uppercase tracking-wider text-xs" data-testid="vermittler-qr-download">
                                <Download className="w-3.5 h-3.5 mr-1.5" /> SVG herunterladen
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Aufklappbarer Bereich: Erläuterung, Aktivierung, KPIs, Showcase */}
            {open && (
                <div className="px-4 pb-4 pt-2 border-t border-gray-100" data-testid="vermittler-share-link-body">
                    <p className="text-xs text-gray-600 mb-4">
                        Teilen Sie diesen Link in WhatsApp, Instagram oder per E-Mail — bei jedem
                        erfolgreichen Verkauf darüber erhalten Sie automatisch <strong>50 Punkte</strong>.
                    </p>

                    {/* Master-Aktivierung */}
                    <label className="flex items-start gap-3 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-sm cursor-pointer" data-testid="link-active-toggle">
                        <input
                            type="checkbox"
                            checked={linkActive}
                            disabled={savingLinkToggle}
                            onChange={(e) => toggleLinkActive(e.target.checked)}
                            className="mt-1 w-4 h-4 accent-[#FFCC00]"
                            data-testid="link-active-checkbox"
                        />
                        <div className="flex-1">
                            <div className="font-bold text-sm">Empfehlungs-Link aktivieren</div>
                            <div className="text-[11px] text-gray-600 mt-0.5">
                                Wenn deaktiviert: Klicks auf Ihren Link werden NICHT mehr gezählt und Besucher landen direkt auf der Startseite — kein Tracking, keine Punkte.
                            </div>
                        </div>
                    </label>

                    {/* Click + conversion stats */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="border border-gray-200 rounded-sm p-3" data-testid="share-stat-clicks">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Klicks (Total)</div>
                            <div className="text-2xl font-black font-mono-num mt-1">{data.click_count || 0}</div>
                        </div>
                        <div className="border border-gray-200 rounded-sm p-3" data-testid="share-stat-conversions">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Vermittelt</div>
                            <div className="text-2xl font-black font-mono-num mt-1 text-[#16A34A]">{data.conversion_count || 0}</div>
                        </div>
                    </div>

                    {/* Public-Showcase Opt-In */}
                    <div className="pt-4 border-t border-gray-100">
                        <label className={`flex items-center gap-3 ${verified ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}>
                            <input
                                type="checkbox"
                                checked={optIn}
                                disabled={!verified || savingOpt}
                                onChange={(e) => toggleOptIn(e.target.checked)}
                                className="w-4 h-4 accent-[#FFCC00] shrink-0"
                                data-testid="showcase-opt-in"
                            />
                            {optIn ? <Globe className="w-4 h-4 text-[#16A34A] shrink-0" /> : <EyeOff className="w-4 h-4 text-gray-400 shrink-0" />}
                            <div className="flex-1 min-w-0">
                                <span className="font-bold text-sm">Im öffentlichen Showcase auf <code>/team</code> erscheinen</span>
                                <span className="text-[11px] text-gray-500 ml-2">
                                    {verified ? "Name, Stadt & Vermittlungen öffentlich — steigert die Klickrate." : "Erst nach genehmigter Identitätsprüfung aktivierbar."}
                                </span>
                            </div>
                        </label>
                    </div>
                </div>
            )}
        </div>
    );
}
